import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import type { ViewType, TabType } from '../../types';

interface UiState {
  currentView: ViewType;
  activeTab: TabType;
  editingTemplateId: string | null;
}

const initialState: UiState = {
  currentView: 'dashboard',
  activeTab: 'list',
  editingTemplateId: null,
};

export const { actions, reducer } = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setView: (state, action: PayloadAction<ViewType>) => {
      state.currentView = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<TabType>) => {
      state.activeTab = action.payload;
    },
    setEditingTemplateId: (state, action: PayloadAction<string | null>) => {
      state.editingTemplateId = action.payload;
    },
  },
});

// Define action interfaces for the custom hook
interface UIActions {
  setView: (view: ViewType) => void;
  setActiveTab: (tab: TabType) => void;
  setEditingTemplateId: (templateId: string | null) => void;
}

// Combined type for the custom hook
type UISlice = UiState & UIActions;

// Custom hook that combines state and actions
export const useUISlice = (): UISlice => {
  const dispatch = useDispatch();
  return {
    // State selectors
    currentView: useSelector((state: RootState) => state.ui.currentView),
    activeTab: useSelector((state: RootState) => state.ui.activeTab),
    editingTemplateId: useSelector((state: RootState) => state.ui.editingTemplateId),

    // Action dispatchers
    setView: (view) => dispatch(actions.setView(view)),
    setActiveTab: (tab) => dispatch(actions.setActiveTab(tab)),
    setEditingTemplateId: (templateId) => dispatch(actions.setEditingTemplateId(templateId)),
  };
};

export default reducer;
